import { useHistoricoDeTransicoes } from '../../../api/queries/gmuds'
import { dataHora } from '../../../lib/datas'
import { TEXTOS } from '../../../lib/glossario'
import type { RegistroTransicao, StatusGmud } from '../../../types/gmud'

/** Os comandos que são decisão do comitê, ou o que a segue de perto. */
const COMANDOS_DE_DECISAO = new Set([
  'DECIDIR_CAB_APROVAR',
  'DECIDIR_CAB_REJEITAR',
  'DECIDIR_CAB_SOLICITAR_INFORMACOES',
  'REVOGAR_APROVACAO',
  'REPROVAR_VERIFICACAO',
  'CANCELAR',
])

/** Os estados em que a última decisão é o que a pessoa precisa ler antes de agir. */
const ESTADOS_COM_DECISAO = new Set<StatusGmud>([
  'AGUARDANDO_AJUSTES',
  'APROVADA',
  'RECUSADA',
  'REVERTIDA_ROLLBACK',
  'CANCELADA',
  'EM_EXECUCAO',
])

/**
 * A última decisão do comitê, à vista — e não só na quarta aba.
 *
 * A contratada abria a solicitação devolvida, via "Aguardando ajustes" e o botão de reenviar, e o
 * que o comitê PEDIU estava na aba "Histórico", a última. O mesmo para "Recusada" e "Aprovada": a
 * fundamentação do presidente — o ato administrativo — não estava à vista de quem responde a ele.
 * Não é ata nem rito (decisão de 19/08): é a transição já gravada na trilha, mostrada onde se
 * decide e onde se responde.
 *
 * @param props a solicitação e o estado dela
 * @returns o cartão, ou nada quando o estado não tem decisão a ler
 */
export function UltimaDecisao({ gmudId, status }: { gmudId: number; status: StatusGmud }) {
  const { data } = useHistoricoDeTransicoes(gmudId)
  if (!ESTADOS_COM_DECISAO.has(status) || !data) {
    return null
  }
  const decisao = ultimaDecisao(data)
  if (!decisao) {
    return null
  }
  const t = TEXTOS.ultimaDecisao
  return (
    <section className="cartao space-y-2 border-l-4 border-l-niteroi-600 p-5" aria-label={t.titulo}>
      <h2 className="text-base font-semibold text-slate-900">{t.titulo}</h2>
      <p className="text-sm text-slate-800">
        <span className="font-medium">{decisao.comandoRotulo ?? decisao.comando}</span>
        <span className="text-slate-500">
          {' '}
          — {t.porQuem(decisao.executadoPorNome ?? '—', dataHora(decisao.ocorridoEm))}
        </span>
      </p>
      <p className="max-w-prose whitespace-pre-line text-sm text-slate-700">{decisao.motivo ?? t.semMotivo}</p>
      <p className="text-xs text-slate-500">{t.ajuda}</p>
    </section>
  )
}

/** A decisão mais recente na trilha; a trilha chega em ordem, mas não se confia na ordem. */
function ultimaDecisao(historico: RegistroTransicao[]): RegistroTransicao | null {
  let ultima: RegistroTransicao | null = null
  for (const registro of historico) {
    if (registro.comando && COMANDOS_DE_DECISAO.has(registro.comando)) {
      if (!ultima || registro.ocorridoEm > ultima.ocorridoEm) {
        ultima = registro
      }
    }
  }
  return ultima
}
